// UploadPlanilha.jsx (patch com suporte a mentor entrada/saída)
import React, { useState } from "react";
import * as XLSX from "xlsx";
import styles from "./uploadplanilha.module.css";

const extensoesPermitidas = [".xlsx", ".xls"];

const UploadPlanilha = ({
  onEntryDataProcessed,
  onLeaveDataProcessed,
  onMentorDataProcessed,   // mantém um único callback
  PJ = false,
  setPrograma,
  setEmpresa,
  setConsolidado,
  showLoader,
  hideLoader,
}) => {
  const [fileEntry, setFileEntry] = useState(null);
  const [fileLeave, setFileLeave] = useState(null);

  // >>> novos estados para mentor
  const [fileMentorEntry, setFileMentorEntry] = useState(null);
  const [fileMentorLeave, setFileMentorLeave] = useState(null);

  const [programaLocal, setProgramaLocal] = useState("");
  const [empresaLocal, setEmpresaLocal] = useState("");
  const [modoConsolidado, setModoConsolidado] = useState(true);
  const [incluirMentor, setIncluirMentor] = useState(false);

  const validarExtensao = (nomeArquivo) =>
    extensoesPermitidas.includes(
      nomeArquivo.slice(nomeArquivo.lastIndexOf(".")).toLowerCase()
    );

  const onPick = (setter, tipo) => (e) => {
    const file = e.target.files[0];
    if (file && !validarExtensao(file.name)) {
      alert(`Arquivo de ${tipo} inválido. Use .xlsx ou .xls`);
      return;
    }
    setter(file);
  };

  const processFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const binaryStr = e.target.result;
          const workbook = XLSX.read(binaryStr, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(sheet);
          resolve(data);
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsBinaryString(file);
    });

  const processFiles = async () => {
    // Necessidades por modo
    if (!fileEntry || (modoConsolidado && !fileLeave)) return;
    if (!programaLocal || (PJ && !empresaLocal)) {
      alert("Preencha todos os campos obrigatórios.");
      return;
    }

    // Mentor: entrada é obrigatória se incluirMentor; e no consolidado, também a de saída
    if (
      incluirMentor &&
      (!fileMentorEntry || (modoConsolidado && !fileMentorLeave))
    ) {
      alert(
        modoConsolidado
          ? "Selecione as planilhas de Mentor (Entrada e Saída)."
          : "Selecione a planilha de Mentor (Entrada)."
      );
      return;
    }

    showLoader("Lendo planilhas");

    try {
      // Participante
      const dadosEntrada = await processFile(fileEntry);
      onEntryDataProcessed(dadosEntrada);

      if (modoConsolidado) {
        const dadosSaida = await processFile(fileLeave);
        onLeaveDataProcessed(dadosSaida);
      } else {
        onLeaveDataProcessed([]);
      }

      // Mentor
      if (incluirMentor) {
        const mentorEntrada = await processFile(fileMentorEntry);
        if (modoConsolidado && fileMentorLeave) {
          const mentorSaida = await processFile(fileMentorLeave);
          onMentorDataProcessed &&
            onMentorDataProcessed({ entrada: mentorEntrada, saida: mentorSaida });
        } else {
          onMentorDataProcessed &&
            onMentorDataProcessed({ entrada: mentorEntrada, saida: [] });
        }
      } else {
        onMentorDataProcessed && onMentorDataProcessed({ entrada: [], saida: [] });
      }

      setPrograma(programaLocal);
      if (PJ) setEmpresa(empresaLocal);
      setConsolidado(modoConsolidado);
    } catch (error) {
      console.error("Erro ao ler planilhas:", error);
      alert("Erro ao processar os arquivos. Verifique se os arquivos estão corretos.");
    } finally {
      hideLoader();
    }
  };

  return (
    <div className={styles.uploadContainer}>
      <h2 className={styles.sectionTitle}>Upload das Planilhas</h2>

      <div className={styles.tipoSelector}>
        <button
          type="button"
          className={!modoConsolidado ? styles.active : ""}
          disabled={!modoConsolidado ? true : false}
          onClick={() => setModoConsolidado(false)}
        >
          Apenas Entrada
        </button>
        <button
          type="button"
          className={modoConsolidado ? styles.active : ""}
          disabled={modoConsolidado ? true : false}
          onClick={() => setModoConsolidado(true)}
        >
          Entrada + Saída
        </button>

        <button
          type="button"
          className={incluirMentor ? styles.active : ""}
          onClick={() => setIncluirMentor((v) => !v)}
        >
          {incluirMentor ? "Com Planilha do Mentor" : "Adicionar Planilha do Mentor"}
        </button>
      </div>

      <label>Nome do Programa:</label>
      <input
        type="text"
        placeholder="Ex: Trilha de Liderança"
        value={programaLocal}
        onChange={(e) => setProgramaLocal(e.target.value)}
        className={styles.inputField}
      />

      {PJ && (
        <>
          <label>Nome da Empresa:</label>
          <input
            type="text"
            placeholder="Ex: XPTO Ltda."
            value={empresaLocal}
            onChange={(e) => setEmpresaLocal(e.target.value)}
            className={styles.inputField}
          />
        </>
      )}

      {/* Entrada participante */}
      <label className={styles.fileInputWrapper}>
        <span className={styles.fileLabel}>Selecionar Planilha de Entrada</span>
        <input type="file" accept=".xlsx,.xls" onChange={onPick(setFileEntry, "entrada")} className={styles.fileInput}/>
        {fileEntry && <span className={styles.fileName}>Arquivo selecionado: {fileEntry.name}</span>}
      </label>

      {/* Saída participante */}
      {modoConsolidado && (
        <label className={styles.fileInputWrapper}>
          <span className={styles.fileLabel}>Selecionar Planilha de Saída</span>
          <input type="file" accept=".xlsx,.xls" onChange={onPick(setFileLeave, "saída")} className={styles.fileInput}/>
          {fileLeave && <span className={styles.fileName}>Arquivo selecionado: {fileLeave.name}</span>}
        </label>
      )}

      {/* Mentor */}
      {incluirMentor && (
        <>
          <label className={styles.fileInputWrapper}>
            <span className={styles.fileLabel}>Selecionar Planilha do Mentor (Entrada)</span>
            <input type="file" accept=".xlsx,.xls" onChange={onPick(setFileMentorEntry, "mentor (entrada)")} className={styles.fileInput}/>
            {fileMentorEntry && <span className={styles.fileName}>Arquivo selecionado: {fileMentorEntry.name}</span>}
          </label>

          {modoConsolidado && (
            <label className={styles.fileInputWrapper}>
              <span className={styles.fileLabel}>Selecionar Planilha do Mentor (Saída)</span>
              <input type="file" accept=".xlsx,.xls" onChange={onPick(setFileMentorLeave, "mentor (saída)")} className={styles.fileInput}/>
              {fileMentorLeave && <span className={styles.fileName}>Arquivo selecionado: {fileMentorLeave.name}</span>}
            </label>
          )}
        </>
      )}

      <button
        onClick={processFiles}
        className={styles.processButton}
        disabled={
          !fileEntry ||
          (modoConsolidado && !fileLeave) ||
          !programaLocal ||
          (PJ && !empresaLocal) ||
          (incluirMentor &&
            (!fileMentorEntry || (modoConsolidado && !fileMentorLeave)))
        }
      >
        Ler Planilhas
      </button>
    </div>
  );
};

export default UploadPlanilha;